import { useState, useRef, useEffect, ReactNode } from 'react';
import Icon from './Icon';
import { cn } from '@/utils/cn';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  icon?: ReactNode;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  searchable?: boolean;
  label?: string;
  required?: boolean;
}

export function Select({
  options,
  value,
  onChange,
  placeholder = 'Seçiniz...',
  disabled = false,
  error = false,
  className,
  size = 'md',
  searchable = false,
  label,
  required = false,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const normalizedQuery = searchQuery.toLocaleLowerCase('tr-TR').replace(/ı/g, 'i');
  const filteredOptions = searchable
    ? options.filter((opt) => {
        const labelStr = typeof opt.label === 'string' ? opt.label : String(opt.label);
        const normalizedLabel = labelStr.toLocaleLowerCase('tr-TR').replace(/ı/g, 'i');
        return normalizedLabel.includes(normalizedQuery);
      })
    : options;

  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-5 py-3 text-base',
    lg: 'px-6 py-4 text-lg',
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      if (searchable && searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, searchable]);

  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && optionsRef.current) {
      const optionElement = optionsRef.current.children[focusedIndex] as HTMLElement;
      if (optionElement) {
        optionElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [focusedIndex, isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        if (!isOpen) {
          e.preventDefault();
          setIsOpen(true);
        } else if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
          e.preventDefault();
          handleSelect(filteredOptions[focusedIndex].value);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev,
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchQuery('');
        setFocusedIndex(-1);
        break;
    }
  };

  const handleSelect = (optionValue: string | number) => {
    if (onChange) {
      onChange(optionValue);
    }
    setIsOpen(false);
    setSearchQuery('');
    setFocusedIndex(-1);
  };

  return (
    <div className={cn('relative w-full', className)}>
      {label && (
        <label className="input-label">
          {label}
          {required && <span className="text-[#E63946] ml-1">*</span>}
        </label>
      )}
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          className={cn(
            'input-base',
            'flex items-center justify-between',
            'cursor-pointer',
            sizeClasses[size],
            error && 'input-error',
            disabled && 'bg-gray-50 text-gray-400 cursor-not-allowed',
            !disabled && 'text-gray-900',
            isOpen && 'ring-2 ring-primary/30 border-primary',
          )}
        >
          <span className={cn('flex items-center gap-2 flex-1 text-left min-w-0', !selectedOption && 'text-gray-400')}>
            {selectedOption?.icon && <span className="flex-shrink-0">{selectedOption.icon}</span>}
            <span className="truncate">{selectedOption?.label || placeholder}</span>
          </span>
          <div className="flex-shrink-0 ml-3 pr-1 flex items-center">
            <Icon
              name={isOpen ? 'close' : 'chevron-down'}
              size={20}
              className={cn('transition-transform duration-200 text-gray-500', isOpen && 'rotate-180', disabled && 'text-gray-400')}
            />
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            {searchable && (
              <div className="p-3 border-b border-gray-200 bg-gray-50">
                <div className="relative">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setFocusedIndex(-1);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Ara..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#E63946]/30 focus:border-[#E63946]"
                  />
                  <Icon
                    name="search"
                    size={18}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                </div>
              </div>
            )}

            <div
              ref={optionsRef}
              className="max-h-60 overflow-y-auto overscroll-contain"
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const isSelected = option.value === value;
                  const isFocused = index === focusedIndex;
                  const isDisabled = option.disabled || disabled;

                  return (
                    <button
                      key={String(option.value)}
                      type="button"
                      onClick={() => !isDisabled && handleSelect(option.value)}
                      disabled={isDisabled}
                      onMouseEnter={() => setFocusedIndex(index)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-left',
                        'transition-colors duration-150',
                        'first:rounded-t-2xl last:rounded-b-2xl',
                        isSelected
                          ? 'bg-[#E63946]/10 text-[#E63946] font-medium'
                          : 'text-gray-900',
                        isFocused && !isSelected && 'bg-gray-50',
                        isDisabled
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50 cursor-pointer',
                      )}
                    >
                      {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
                      <span className="flex-1">{option.label}</span>
                      {isSelected && (
                        <Icon name="check" size={18} className="flex-shrink-0 text-[#E63946]" />
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  {searchable ? 'Sonuç bulunamadı' : 'Seçenek yok'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-sm text-red-600">Bu alan zorunludur</p>
      )}
    </div>
  );
}
